/**
 * LOUD/QUIET Input — labelled field with a hard-shadow wrap and an optional appended action
 * button (arrow submit, etc). Mirrors the design-doc field card.
 *
 * Ported 1:1 from the compiled `_ds_bundle.js`. Structure: column wrapper > optional <label> >
 * a flex row that carries the shadow (input + optional 52px action button, borders collapsed
 * between them) > optional helper <p>.
 *
 * Non-obvious decisions:
 * - The bundle sets `outline: 0` on the field and never puts anything back, so a keyboard user
 *   gets no focus indicator at all. That is an accessibility defect, not a visual choice, so
 *   the 4px blue ring is restored via a `:focus-visible` check in onFocus (same inline-handler
 *   technique the Button uses). Nothing about the resting appearance changes.
 * - The bundle derives `id` from the label text, so two fields labelled the same way (or
 *   differing only in whitespace) collide and both <label>s drive the first field. The id is
 *   generated with useId here instead, which is per-instance; an explicit `id` still wins.
 * - The bundle leaves the trailing action button live while `disabled` is set — the row only
 *   dims. That is a defect rather than a visual choice, so the button is disabled with the
 *   field. Callers who want a live action on a disabled field should render it themselves.
 * - `style` lands on the outer column wrapper; `...rest` lands on the <input>.
 */
import * as React from 'react';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'style' | 'children'> {
  /** Field label. Also seeds the auto-generated id when `id` is omitted. */
  label?: string;
  /** Muted line under the field. Human microcopy, not error text. */
  helper?: React.ReactNode;
  /** Content of the trailing action button, e.g. "→". Omit to render no button. */
  action?: React.ReactNode;
  /** Click handler for the trailing action button. */
  onAction?: React.MouseEventHandler<HTMLButtonElement>;
  /** Accessible name for the trailing action button. @default "Submit" */
  actionLabel?: string;
  /** @default "text" */
  type?: React.HTMLInputTypeAttribute;
  /** @default false */
  disabled?: boolean;
  /** Applied to the outer column wrapper, not the field. */
  style?: React.CSSProperties;
}

/** `:focus-visible` is unsupported on a few old engines; fall back to always showing the ring. */
function isFocusVisible(el: Element): boolean {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
}

export function Input({
  label,
  id,
  type = 'text',
  placeholder,
  helper,
  action,
  onAction,
  actionLabel = 'Submit',
  defaultValue,
  value,
  onChange,
  disabled = false,
  style,
  onFocus,
  onBlur,
  ...rest
}: InputProps) {
  const autoId = React.useId();
  const fieldId = id || `lq-${autoId}`;

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
    if (isFocusVisible(e.currentTarget)) {
      e.currentTarget.style.outline = 'var(--lq-focus-ring)';
      e.currentTarget.style.outlineOffset = 'var(--lq-focus-offset, 3px)';
    }
    onFocus?.(e);
  };
  const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    // Back to the bundle's resting value, not "" — the style object declares `outline: 0`.
    e.currentTarget.style.outline = '0';
    e.currentTarget.style.outlineOffset = '';
    onBlur?.(e);
  };
  const handleActionFocus: React.FocusEventHandler<HTMLButtonElement> = (e) => {
    if (isFocusVisible(e.currentTarget)) {
      e.currentTarget.style.outline = 'var(--lq-focus-ring)';
      e.currentTarget.style.outlineOffset = 'var(--lq-focus-offset, 3px)';
    }
  };
  const handleActionBlur: React.FocusEventHandler<HTMLButtonElement> = (e) => {
    e.currentTarget.style.outline = '';
    e.currentTarget.style.outlineOffset = '';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', ...style }}>
      {label ? (
        <label
          htmlFor={fieldId}
          style={{
            display: 'block',
            fontFamily: 'var(--lq-font-sans)',
            fontWeight: 800,
            fontSize: 13,
            marginBottom: 9,
          }}
        >
          {label}
        </label>
      ) : null}
      {/* The shadow sits on the row, so the field and its action button cast one shared shadow. */}
      <div
        style={{
          display: 'flex',
          boxShadow: 'var(--lq-shadow-s)',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <input
          {...rest}
          id={fieldId}
          type={type}
          placeholder={placeholder}
          defaultValue={defaultValue}
          value={value}
          onChange={onChange}
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            minWidth: 0,
            width: '100%',
            border: 'var(--lq-border)',
            // Collapse the shared edge so the pair reads as one 2px-bordered object.
            borderRight: action ? 0 : 'var(--lq-border)',
            background: 'var(--lq-white)',
            padding: 14,
            outline: 0,
            fontFamily: 'var(--lq-font-sans)',
            fontSize: 15,
            color: 'var(--lq-ink)',
          }}
        />
        {action ? (
          <button
            type="button"
            aria-label={actionLabel}
            onClick={onAction}
            disabled={disabled}
            onFocus={handleActionFocus}
            onBlur={handleActionBlur}
            style={{
              width: 52,
              flex: '0 0 52px',
              border: 'var(--lq-border)',
              background: 'var(--lq-blue)',
              color: 'var(--lq-white)',
              fontWeight: 900,
              fontSize: 18,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {/* NOTE: the bundle does not mark this glyph aria-hidden; the button's aria-label
                already overrides its content for assistive tech, so it is left as shipped. */}
            {action}
          </button>
        ) : null}
      </div>
      {helper ? (
        <p
          style={{
            margin: '9px 0 0',
            fontFamily: 'var(--lq-font-sans)',
            fontSize: 12,
            color: 'var(--lq-text-muted)',
          }}
        >
          {helper}
        </p>
      ) : null}
    </div>
  );
}

export default Input;
